import React, { useState, useEffect } from "react";
import {
  Container,
  VStack,
  Heading,
  Box,
  Text,
  Button,
  FormControl,
  FormLabel,
  Input,
  Grid,
  useToast,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Alert,
  AlertIcon,
  Tag,
  HStack,
  Image,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  useDisclosure,
  Center,
  Spinner,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import { useTournament } from "../contexts/TournamentContext";
import {
  getGolfPoolEntries,
  createGolfPoolEntry,
} from "../services/firestore";
import { normalizePoolTeamNames } from "../utils/normalizePoolTeamNames";
import venmoQr from "../assets/images/venmo_j.jpeg";

const ENTRY_FEE = 25;

/** Display label for pool team-selection buttons (disambiguate shared last names). */
const getPoolButtonPlayerLabel = (playerName) => {
  if (playerName === "Ellis Opie") return "E. Opie";
  if (playerName === "Brett Opie") return "B. Opie";
  if (playerName === "Ryan Opie") return "R. Opie";
  if (playerName === "Aaron Campbell") return "Aaron Campbell";
  const parts = playerName.trim().split(/\s+/);
  return parts[parts.length - 1];
};

const GolfPoolPage = () => {
  const { teams, poolFlyer, year, label, basePath, poolMaxTeams, poolOpen } =
    useTournament();
  // Gate submissions on poolOpen — independent of readOnly so live scoring stays open.
  const isArchiveClosed = year === "2025";
  const isPoolClosed = poolOpen === false;
  const toast = useToast();
  const {
    isOpen: isFlyerOpen,
    onOpen: onFlyerOpen,
    onClose: onFlyerClose,
  } = useDisclosure();
  const {
    isOpen: isConfirmOpen,
    onOpen: onConfirmOpen,
    onClose: onConfirmClose,
  } = useDisclosure();
  const {
    isOpen: isVenmoOpen,
    onOpen: onVenmoOpen,
    onClose: onVenmoClose,
  } = useDisclosure();
  const cancelRef = React.useRef();
  const [selectedTeams, setSelectedTeams] = useState([]);
  const [participantName, setParticipantName] = useState("");
  const [participantEmail, setParticipantEmail] = useState("");
  const [participantPhone, setParticipantPhone] = useState("");
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();
  // Load entries from Firebase on component mount
  useEffect(() => {
    const loadEntries = async () => {
      try {
        setLoading(true);
        const firebaseEntries = await getGolfPoolEntries();

        // Convert Firebase timestamp to readable format
        const formattedEntries = firebaseEntries.map((entry) => ({
          ...entry,
          timestamp:
            entry.timestamp?.toDate?.()?.toLocaleString() || entry.timestamp,
        }));

        setEntries(formattedEntries);
      } catch (error) {
        console.error("Error loading entries:", error);
        toast({
          title: "Error",
          description: "Failed to load entries. Please refresh the page.",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      } finally {
        setLoading(false);
      }
    };

    loadEntries();
  }, [toast]);

  const handleTeamSelection = (teamId) => {
    const team = teams.find((t) => t.id === parseInt(teamId));
    if (!team) return;

    if (selectedTeams.includes(team.name)) {
      setSelectedTeams(selectedTeams.filter((t) => t !== team.name));
    } else {
      if (selectedTeams.length >= poolMaxTeams) {
        toast({
          title: "Selection Limit Reached",
          description: `You can only select ${poolMaxTeams} teams`,
          status: "warning",
          duration: 3000,
          isClosable: true,
        });
        return;
      }
      setSelectedTeams([...selectedTeams, team.name]);
    }
  };

  const validateEntryForm = () => {
    if (isPoolClosed) {
      toast({
        title: "Error",
        description: "Tournament has started, no more entries allowed",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return false;
    }

    if (!participantName) {
      toast({
        title: "Error",
        description: "Please enter your name",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return false;
    }

    if (!participantEmail) {
      toast({
        title: "Error",
        description: "Please enter your email",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return false;
    }

    if (!participantPhone) {
      toast({
        title: "Error",
        description: "Please enter your phone number",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return false;
    }

    if (selectedTeams.length !== poolMaxTeams) {
      toast({
        title: "Error",
        description: `Please select exactly ${poolMaxTeams} teams`,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return false;
    }

    return true;
  };

  const handleSubmitClick = () => {
    if (!validateEntryForm()) return;
    onConfirmOpen();
  };

  const handleConfirmSubmit = async () => {
    onConfirmClose();

    try {
      setSubmitting(true);

      const normalizedTeams = normalizePoolTeamNames(selectedTeams);
      const entryData = {
        participant: participantName,
        email: participantEmail,
        phone: participantPhone,
        teams: normalizedTeams,
      };

      // Save to Firebase (createGolfPoolEntry also normalizes Opie aliases)
      const docRef = await createGolfPoolEntry(entryData);

      // Create the new entry for local state with the Firebase ID
      const newEntry = {
        id: docRef.id,
        participant: participantName,
        email: participantEmail,
        phone: participantPhone,
        teams: normalizedTeams,
        timestamp: new Date().toLocaleString(),
      };

      // Add to local state
      setEntries([newEntry, ...entries]);
      setSelectedTeams([]);
      setParticipantName("");
      setParticipantEmail("");
      setParticipantPhone("");

      toast({
        title: "Entry Submitted",
        description: "Your pool entry has been recorded",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error("Error submitting entry:", error);
      toast({
        title: "Error",
        description: "Failed to submit entry. Please try again.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Container maxW="container.xl" py={6}>
      <VStack spacing={8} align="stretch">
        <Heading size="lg">
          {isArchiveClosed ? `${label} Golf Pool` : "Golf Pool"}
        </Heading>

        {/* Pool Format Info */}
        <Alert status="info" borderRadius="lg" position="relative">
          <AlertIcon />
          <VStack align="start" spacing={0.1} flex="1" pr={4}>
            <Text fontWeight="bold">Pool Format:</Text>
            <Text>• Entry Fee: ${ENTRY_FEE}</Text>
            <Text>• Select {poolMaxTeams} teams from the tournament field</Text>
            <Text>• Best cumulative total score wins</Text>
            <Text>• 50% of pot goes to the winner</Text>
            <Text>• 50% will be donated to Ovarian Cancer Canada</Text>
          </VStack>
          <Box position="absolute" top="4" right="4">
            <Image
              src={poolFlyer}
              alt="Golf Pool Info"
              maxW="50px"
              w="50px"
              cursor="pointer"
              onClick={onFlyerOpen}
              borderRadius="lg"
              boxShadow="md"
              _hover={{
                transform: "scale(1.05)",
                transition: "transform 0.2s",
              }}
            />
          </Box>
        </Alert>

        <Modal isOpen={isFlyerOpen} onClose={onFlyerClose} size="6xl">
          <ModalOverlay />
          <ModalContent bg="transparent" boxShadow="none">
            <ModalCloseButton color="white" size="lg" />
            <Image
              src={poolFlyer}
              alt="Golf Pool Info"
              w="100%"
              maxH="90vh"
              objectFit="contain"
              borderRadius="lg"
            />
          </ModalContent>
        </Modal>

        <AlertDialog
          isOpen={isConfirmOpen}
          leastDestructiveRef={cancelRef}
          onClose={onConfirmClose}
          isCentered
        >
          <AlertDialogOverlay>
            <AlertDialogContent bg="green.800" color="white">
              <AlertDialogHeader fontSize="lg" fontWeight="bold">
                Entry Fee Reminder
              </AlertDialogHeader>
              <AlertDialogBody>
                Have you transferred your entry fee to
                thearinvitational@gmail.com?. If you haven&apos;t please dont
                forget to do so.
              </AlertDialogBody>
              <AlertDialogFooter>
                <Button
                  ref={cancelRef}
                  colorScheme="green"
                  onClick={handleConfirmSubmit}
                  isLoading={submitting}
                >
                  Ok
                </Button>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialogOverlay>
        </AlertDialog>

        <Modal isOpen={isVenmoOpen} onClose={onVenmoClose} isCentered size="sm">
          <ModalOverlay bg="blackAlpha.700" />
          <ModalContent bg="gray.900" color="white" borderRadius="xl" mx={4}>
            <ModalHeader pb={2} fontSize="lg" textAlign="center">
              Pay with Venmo
            </ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <VStack spacing={4}>
                <Image
                  src={venmoQr}
                  alt="Venmo QR code"
                  w="100%"
                  maxW="260px"
                  borderRadius="lg"
                  boxShadow="md"
                />
                <Text textAlign="center" fontSize="sm" color="gray.300">
                  Label your payment{" "}
                  <Text as="span" fontWeight="bold" color="white">
                    AR
                  </Text>
                </Text>
              </VStack>
            </ModalBody>
            <ModalFooter justifyContent="center" pt={2}>
              <Button colorScheme="green" onClick={onVenmoClose} minW="100px">
                Done
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>

        {isPoolClosed ? (
          <Box bg="blue.700" p={6} borderRadius="lg" textAlign="center">
            {year === "2026" ? (
              <>
                <Text color="white" fontWeight="bold">
                  Tournament has ended.
                </Text>
                <Text color="white">Updates via email will come soon.</Text>
              </>
            ) : (
              <>
                <Text color="white">Tournament has started!</Text>
                <Text color="white">
                  The pool is now closed. Thank you for your participation!
                </Text>
                <Text color="white">
                  Live leaderboard for the Golf Pool will be available on the{"  "}
                  <Button
                    colorScheme="blue"
                    variant="outline"
                    onClick={() => navigate(`${basePath}/leaderboard`)}
                  >
                    Leaderboard
                  </Button>{" "}
                  page.
                </Text>
              </>
            )}
          </Box>
        ) : (
          <Box bg="green.700" p={6} borderRadius="lg">
            <Heading size="md" mb={4}>
              Submit Your Entry
            </Heading>
            <VStack spacing={6}>
              <FormControl isRequired>
                <FormLabel>Your Name</FormLabel>
                <Input
                  value={participantName}
                  onChange={(e) => setParticipantName(e.target.value)}
                  placeholder="Enter your full name"
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Email Address</FormLabel>
                <Input
                  type="email"
                  value={participantEmail}
                  onChange={(e) => setParticipantEmail(e.target.value)}
                  placeholder="Enter your email address"
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Phone Number</FormLabel>
                <Input
                  type="tel"
                  value={participantPhone}
                  onChange={(e) => setParticipantPhone(e.target.value)}
                  placeholder="Enter your phone number"
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel>
                  Select {poolMaxTeams} Teams ({selectedTeams.length} selected)
                </FormLabel>
                <Grid
                  templateColumns={{
                    base: "1fr",
                    md: "repeat(2, 1fr)",
                    lg: `repeat(${Math.min(poolMaxTeams, 5)}, 1fr)`,
                  }}
                  gap={4}
                >
                  {teams.map((team) => (
                    <Button
                      key={team.id}
                      variant={
                        selectedTeams.includes(team.name) ? "solid" : "outline"
                      }
                      colorScheme="green"
                      onClick={() => handleTeamSelection(team.id)}
                      size="sm"
                      width="full"
                    >
                      {team.players.map((player, index) => (
                        <span key={player}>
                          {getPoolButtonPlayerLabel(player)} (
                          {team.rankings[index]}){" "}
                          {index < team.players.length - 1 && " /  "}
                        </span>
                      ))}
                    </Button>
                  ))}
                </Grid>
              </FormControl>

              <Button
                colorScheme="green"
                width="full"
                onClick={handleSubmitClick}
                isDisabled={
                  selectedTeams.length !== poolMaxTeams ||
                  !participantName ||
                  !participantEmail ||
                  !participantPhone
                }
                isLoading={submitting}
                loadingText="Submitting..."
              >
                Submit Entry (${ENTRY_FEE})
              </Button>

              <Button
                variant="link"
                color="whiteAlpha.900"
                fontWeight="medium"
                fontSize="sm"
                onClick={onVenmoOpen}
                _hover={{ color: "white", textDecoration: "underline" }}
              >
                Paying from the US? Pay with Venmo
              </Button>
            </VStack>
          </Box>
        )}

        {/* Current Entries */}
        <Box>
          <Heading size="md" mb={4}>
            Current Entries
          </Heading>
          {loading ? (
            <Center py={8}>
              <Spinner size="lg" color="green.500" />
            </Center>
          ) : (
            <Box overflowX="auto">
              <Table variant="simple">
                <Thead>
                  <Tr>
                    <Th color="white">Participant</Th>
                    <Th color="white">Selected Teams</Th>
                    <Th color="white">Timestamp</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {entries.length === 0 ? (
                    <Tr>
                      <Td colSpan={3} textAlign="center" py={8}>
                        <Text color="gray.400">
                          No entries yet. Be the first to join!
                        </Text>
                      </Td>
                    </Tr>
                  ) : (
                    entries.map((entry) => (
                      <Tr key={entry.id}>
                        <Td>{entry.participant}</Td>
                        <Td>
                          <HStack spacing={2} wrap="wrap">
                            {normalizePoolTeamNames(entry.teams).map(
                              (team, index) => (
                                <Tag key={index} colorScheme="green" size="sm">
                                  {team}
                                </Tag>
                              )
                            )}
                          </HStack>
                        </Td>
                        <Td>{entry.timestamp}</Td>
                      </Tr>
                    ))
                  )}
                </Tbody>
              </Table>
            </Box>
          )}
        </Box>
      </VStack>
    </Container>
  );
};

export default GolfPoolPage;
